/**
 * Live-map columnar store (green-field, flag-gated path).
 *
 * Replaces the three plain-object catalogs of `streamingStore` for the live map with per-source, per-channel
 * fixed-capacity typed-array ring buffers fed by the binary Arrow delta-stream (`arrowStreamClient.ts`).
 * No row objects, no 10-field coordinate aliasing, no GC churn: appends are `TypedArray.set()` column copies
 * and playhead lookups are O(log n) binary searches on the `ts` array.
 *
 * Reactivity is a SINGLE rAF-coalesced epoch signal — never per point — so consumers (boats/tracks/overlay/
 * timeline) re-read on the playback clock, not on every frame the socket delivers. This is deliberate: the
 * old per-point WS handler was ~19% of frame time precisely because it churned reactivity per sample.
 *
 * Isolation: this store is used ONLY by the live-map GPU path. It does not touch `streamingStore`,
 * `playbackStore`, or any explore/fleet component.
 */

import { createSignal } from 'solid-js';

import { config } from '@config/env';

import { isRenderYieldActive } from '../utils/renderYield';

/**
 * Epoch cadence while a render-yield window is open (~2fps). Matches `YIELD_PUBLISH_MS` in
 * playbackStore so the playhead and the data it draws back off together.
 */
const LIVE_YIELD_EPOCH_MS = 500;

/** Per-source ring: capacity fixed; when full, advance `start` (drop oldest). All channels are Float64. */
interface SourceRing {
  capacity: number;
  start: number; // physical index of the oldest logical row
  count: number;
  ts: Float64Array;
  channels: Map<string, Float64Array>; // lazily created as channels arrive
}

/** Column-major frame handed in by the decoder: `ts` + any subscribed channels, all as numeric arrays. */
export interface ColumnarFrame {
  sourceIds: Int16Array | number[]; // per-row source id
  ts: Float64Array | number[]; // per-row wall-clock ms
  channels: Record<string, ArrayLike<number>>; // channel -> per-row value (NaN = missing)
  reset?: boolean; // snapshot after a gap: caller may clear affected sources first
}

/**
 * Nominal per-source sample rate used to turn a retention in MINUTES into a slot count.
 *
 * Deliberately above the ~10.7 Hz the fleet actually produces (server_stream/recentStore measures
 * ~139 rows/s across 13 boats): the ring must not come up short for a boat that happens to sample
 * faster, and over-allocating costs only memory, whereas under-allocating silently truncates the
 * oldest end of a window the user explicitly asked to see.
 */
const NOMINAL_SAMPLE_HZ = 12;

/**
 * Slots per source per channel.
 *
 * This is what bounds how far back the live map can be paused or scrubbed with no network read at
 * all — there is no read path older than the ring, so anything shallower than the time-window dial's
 * maximum means scrubbing back renders nothing.
 *
 * It was a flat 4096 ("~3.4 min at 20 Hz", which was wrong twice over: the real cadence is ~10.7 Hz,
 * making it ~6.4 minutes — barely more than the DEFAULT 5-minute track window and a fifth of the
 * 30-minute maximum). Tunable via VITE_LIVE_MAP_RING_MINUTES.
 */
const DEFAULT_CAPACITY = Math.ceil(
  (Number(config.LIVE_MAP_RING_MINUTES) || 32) * 60 * NOMINAL_SAMPLE_HZ
);

/**
 * How far either side of a playhead `bracketFinite` will look for a row that actually carries the requested
 * channels. Bounds the scan across a run of holes; beyond this the caller is better off drawing nothing than
 * interpolating across a gap that long.
 */
const BRACKET_MAX_GAP_MS = 60_000;

const rings = new Map<number, SourceRing>();
const nameById = new Map<number, string>();
const idByName = new Map<string, number>();

const [epoch, setEpoch] = createSignal(0);
let epochScheduled = false;

/** Coalesce reactive notification to one bump per animation frame regardless of frame arrival rate. */
function bumpEpochCoalesced(): void {
  if (epochScheduled) return;
  epochScheduled = true;
  const fire = () => {
    epochScheduled = false;
    setEpoch((v) => v + 1);
  };
  // Interaction takes priority: while a page load is in flight, notify at ~2fps instead of per frame.
  // On a default live map this is THE epoch that matters — LIVE_MAP_GPU is on by default, so boats,
  // tracks and the fleet table all wake off this one signal. The rings are already written; only the
  // notification waits, so nothing goes stale and no sample is dropped. See utils/renderYield.ts.
  if (isRenderYieldActive()) {
    setTimeout(fire, LIVE_YIELD_EPOCH_MS);
    return;
  }
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(0 as unknown as DOMHighResTimeStamp), 16);
  raf(fire);
}

function ensureRing(sourceId: number): SourceRing {
  let ring = rings.get(sourceId);
  if (!ring) {
    ring = {
      capacity: DEFAULT_CAPACITY,
      start: 0,
      count: 0,
      ts: new Float64Array(DEFAULT_CAPACITY),
      channels: new Map(),
    };
    rings.set(sourceId, ring);
  }
  return ring;
}

function ensureChannel(ring: SourceRing, name: string): Float64Array {
  let col = ring.channels.get(name);
  if (!col) {
    col = new Float64Array(ring.capacity).fill(NaN);
    ring.channels.set(name, col);
  }
  return col;
}

function phys(ring: SourceRing, i: number): number {
  return (ring.start + i) % ring.capacity;
}

/** Append one row (already newer than the ring's latest) into a source ring, advancing/overwriting. */
function pushRow(ring: SourceRing, ts: number, values: Record<string, number>): void {
  const at = ring.count < ring.capacity ? phys(ring, ring.count) : ring.start;
  ring.ts[at] = ts;
  for (const [name, val] of Object.entries(values)) {
    ensureChannel(ring, name)[at] = val;
  }
  // any channels not present in this row keep their prior slot value; overwrite with NaN to avoid stale reads
  for (const [name, col] of ring.channels) {
    if (!(name in values)) col[at] = NaN;
  }
  if (ring.count < ring.capacity) ring.count++;
  else ring.start = (ring.start + 1) % ring.capacity;
}

/**
 * Hard ceiling on a grown ring, in slots.
 *
 * `hydrateOlder` grows a ring to make room for fetched history, so without a ceiling a user
 * scrubbing back through a long day could walk a single boat's buffers up to hundreds of MB.
 * Sized off the retention knob so the two move together.
 */
const MAX_CAPACITY = DEFAULT_CAPACITY * 6;

/** Re-home a ring into larger arrays, normalising `start` to 0 and preserving logical order. */
function growRing(ring: SourceRing, minCapacity: number): void {
  const next = Math.min(MAX_CAPACITY, Math.max(minCapacity, ring.capacity * 2));
  if (next <= ring.capacity) return;
  const ts = new Float64Array(next);
  for (let i = 0; i < ring.count; i++) ts[i] = ring.ts[phys(ring, i)];
  const channels = new Map<string, Float64Array>();
  for (const [name, col] of ring.channels) {
    const grown = new Float64Array(next).fill(NaN);
    for (let i = 0; i < ring.count; i++) grown[i] = col[phys(ring, i)];
    channels.set(name, grown);
  }
  ring.ts = ts;
  ring.channels = channels;
  ring.capacity = next;
  ring.start = 0;
}

/** Write one row at a LOGICAL index, clearing channels the row does not carry (NaN = hole). */
function writeRowAt(ring: SourceRing, logicalIdx: number, ts: number, values: Record<string, number>): void {
  const at = phys(ring, logicalIdx);
  ring.ts[at] = ts;
  for (const name of Object.keys(values)) ensureChannel(ring, name)[at] = values[name];
  for (const [name, col] of ring.channels) {
    if (!(name in values)) col[at] = NaN;
  }
}

function latestTsOf(ring: SourceRing): number | null {
  return ring.count === 0 ? null : ring.ts[phys(ring, ring.count - 1)];
}

/** True when every one of `cols` holds a finite value at logical row `i`. */
function rowHasAll(ring: SourceRing, i: number, cols: Float64Array[]): boolean {
  const p = phys(ring, i);
  for (const col of cols) {
    if (!Number.isFinite(col[p])) return false;
  }
  return true;
}

/** The requested channels' columns, or null if any of them has never arrived for this source. */
function columnsFor(ring: SourceRing, channels: readonly string[]): Float64Array[] | null {
  const cols: Float64Array[] = [];
  for (const name of channels) {
    const col = ring.channels.get(name);
    if (!col) return null;
    cols.push(col);
  }
  return cols;
}

/** Lowest logical index with ts >= target (binary search over sorted logical order). */
function lowerBound(ring: SourceRing, target: number): number {
  let lo = 0;
  let hi = ring.count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ring.ts[phys(ring, mid)] >= target) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

export const liveColumnarStore = {
  /** Reactive epoch — consumers `void liveColumnarStore.epoch()` to track that new data exists. */
  epoch,

  /**
   * REPLACE the name<->id registry with the server's current map.
   *
   * Deliberately not a merge. The server always sends the complete registry, and a boat that drops out of
   * it is a boat the server is no longer streaming this session. Merging kept de-registered sources
   * resolvable forever, which defeated the "skip sources not currently streamed" guard in GlBoatLayer /
   * LiveMapContainer — after a simulation stopped, its boats still resolved by name and kept rendering at
   * their last replayed position. Rings for sources that left the registry are dropped with them.
   */
  setSourceRegistry(map: Record<string, string>): void {
    const nextById = new Map<number, string>();
    const nextByName = new Map<string, number>();
    for (const [idStr, name] of Object.entries(map)) {
      const id = Number(idStr);
      if (!Number.isFinite(id)) continue;
      const up = String(name).toUpperCase().trim();
      nextById.set(id, up);
      nextByName.set(up, id);
    }
    for (const id of [...rings.keys()]) {
      if (!nextById.has(id)) rings.delete(id);
    }
    nameById.clear();
    idByName.clear();
    for (const [id, up] of nextById) nameById.set(id, up);
    for (const [up, id] of nextByName) idByName.set(up, id);
    bumpEpochCoalesced();
  },
  sourceName(id: number): string | undefined {
    return nameById.get(id);
  },
  sourceId(name: string): number | undefined {
    return idByName.get(String(name).toUpperCase().trim());
  },

  /** Ingest a decoded columnar frame. Rows must be ascending in `ts` per source (server guarantees this). */
  appendFrame(frame: ColumnarFrame): void {
    const n = frame.ts.length;
    if (n === 0) return;
    const chanNames = Object.keys(frame.channels);
    // A reset frame is a re-snapshot, not a continuation: the server's cursor for these sources fell outside
    // its retention (or the session was reset), so whatever we hold for them is a disjoint older run. Drop
    // those rings first, otherwise the monotonic guard below silently discards every re-snapshotted row that
    // predates our stale tail and the boat freezes at its last old position.
    if (frame.reset) {
      for (let i = 0; i < n; i++) rings.delete(frame.sourceIds[i] as number);
    }
    for (let i = 0; i < n; i++) {
      const sid = frame.sourceIds[i] as number;
      const ts = frame.ts[i] as number;
      if (!Number.isFinite(ts)) continue;
      const ring = ensureRing(sid);
      const last = latestTsOf(ring);
      if (last != null && !(ts > last)) continue; // drop out-of-order / dupes
      const values: Record<string, number> = {};
      for (const name of chanNames) values[name] = frame.channels[name][i] as number;
      pushRow(ring, ts, values);
    }
    bumpEpochCoalesced();
  },

  /**
   * Merge fetched history into a source's ring — anything it does not already hold.
   *
   * Two distinct holes need filling, and only one of them is at the start:
   *  - BEFORE the ring. The stream fills forward from the moment the page subscribed, so
   *    everything earlier renders as empty water however long the session has been running.
   *  - INSIDE the ring. A socket that drops and resumes without a `reset` leaves rows on both
   *    sides of the outage and nothing in between, so the track has a gap in the middle of the
   *    window. A prepend-only backfill cannot reach that, which is why this merges rather than
   *    unshifts.
   *
   * RETAINED ROWS WIN on a tie. The live tail is full-rate and unresampled where a hydrate is
   * typically decimated, so where both have an instant the streamed sample is the better one — and
   * taking both would put two different samples of the same instant in one column.
   *
   * Everything lands in the SAME rings the stream feeds, so a hydrated span and a streamed span are
   * indistinguishable to `trackCoords` / `windowRows` / `bracketFinite`, and there is no second
   * buffer for anything downstream to arbitrate between.
   *
   * @returns how many rows were actually added.
   */
  hydrateHistory(sourceId: number, frame: ColumnarFrame): number {
    const n = frame.ts.length;
    if (n === 0) return 0;
    const ring = ensureRing(sourceId);
    const chanNames = Object.keys(frame.channels);

    // Incoming rows: finite, ascending, de-duplicated on exact ts.
    const incoming: Array<{ ts: number; values: Record<string, number> }> = [];
    for (let i = 0; i < n; i++) {
      const ts = frame.ts[i] as number;
      if (!Number.isFinite(ts)) continue;
      const values: Record<string, number> = {};
      for (const name of chanNames) values[name] = frame.channels[name][i] as number;
      incoming.push({ ts, values });
    }
    if (incoming.length === 0) return 0;
    incoming.sort((a, b) => a.ts - b.ts);

    // Snapshot what we hold; the ring is about to be rewritten in place.
    const heldTs = new Float64Array(ring.count);
    for (let i = 0; i < ring.count; i++) heldTs[i] = ring.ts[phys(ring, i)];
    const heldCols = new Map<string, Float64Array>();
    for (const [name, col] of ring.channels) {
      const copy = new Float64Array(ring.count);
      for (let i = 0; i < ring.count; i++) copy[i] = col[phys(ring, i)];
      heldCols.set(name, copy);
    }

    // Two-pointer merge; a retained row consumes any incoming row at the same instant.
    const mergedTs: number[] = [];
    const mergedFrom: Array<{ held: number } | { add: number }> = [];
    let h = 0;
    let a = 0;
    let addedCount = 0;
    while (h < heldTs.length || a < incoming.length) {
      if (a >= incoming.length || (h < heldTs.length && heldTs[h] <= incoming[a].ts)) {
        if (a < incoming.length && heldTs[h] === incoming[a].ts) a++; // tie: keep the retained row
        mergedTs.push(heldTs[h]);
        mergedFrom.push({ held: h });
        h++;
      } else {
        mergedTs.push(incoming[a].ts);
        mergedFrom.push({ add: a });
        a++;
        addedCount++;
      }
    }
    if (addedCount === 0) return 0;

    if (mergedTs.length > ring.capacity) growRing(ring, mergedTs.length);
    // Still short: drop from the OLDEST end, keeping the rows nearest the live edge.
    const overflow = Math.max(0, mergedTs.length - ring.capacity);
    if (overflow > 0) addedCount -= mergedFrom.slice(0, overflow).filter((m) => 'add' in m).length;

    ring.start = 0;
    ring.count = mergedTs.length - overflow;
    for (const name of chanNames) ensureChannel(ring, name);
    for (let i = 0; i < ring.count; i++) {
      const src = mergedFrom[i + overflow];
      if ('held' in src) {
        const values: Record<string, number> = {};
        for (const [name, col] of heldCols) values[name] = col[src.held];
        writeRowAt(ring, i, mergedTs[i + overflow], values);
      } else {
        writeRowAt(ring, i, mergedTs[i + overflow], incoming[src.add].values);
      }
    }

    bumpEpochCoalesced();
    return Math.max(0, addedCount);
  },

  /**
   * Largest interval between consecutive retained samples inside [startMs,endMs], in ms.
   *
   * Lets a caller tell "this span is densely covered" from "there is a hole in the middle of it".
   * A socket that drops and resumes WITHOUT a `reset` leaves rows either side of the outage and
   * nothing between, so the ring can span the whole window and still be missing most of it —
   * checking only the oldest timestamp would report that as fully covered.
   *
   * Returns Infinity when the source holds nothing, and 0 when it holds fewer than two rows in
   * range (no interval to measure).
   */
  maxGapWithin(sourceId: number, startMs: number, endMs: number): number {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return Number.POSITIVE_INFINITY;
    let prev = NaN;
    let max = 0;
    for (let i = lowerBound(ring, startMs); i < ring.count; i++) {
      const t = ring.ts[phys(ring, i)];
      if (t > endMs) break;
      if (Number.isFinite(prev)) max = Math.max(max, t - prev);
      prev = t;
    }
    return max;
  },

  /** Oldest retained timestamp for one source (null when it holds nothing) — the hydration frontier. */
  oldestTsFor(sourceId: number): number | null {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return null;
    return ring.ts[phys(ring, 0)];
  },

  /** Max latest timestamp across the given sources (feeds getLiveDisplayTargetMs in GPU mode). */
  latestTs(sourceIds: Set<number>): number | null {
    let latest: number | null = null;
    for (const id of sourceIds) {
      const ring = rings.get(id);
      if (!ring) continue;
      const t = latestTsOf(ring);
      if (t != null && (latest == null || t > latest)) latest = t;
    }
    return latest;
  },

  /**
   * Latest timestamp across EVERY streamed source — the live data edge, with no id mapping.
   *
   * `latestTs` above takes the caller's ids and looks them up directly, but rings are keyed by the
   * server's stream-assigned frame id, not the app's `source_id`; correct callers resolve by NAME
   * first (see the frameIdBySelected loops in LiveMapContainer / LiveMapUPlotTimeSeries). For the
   * question "has the feed produced anything newer?" that mapping is irrelevant — every ring in this
   * store is a source the server is streaming this session — so asking across all of them avoids the
   * id-collision trap entirely.
   */
  latestTsAny(): number | null {
    let latest: number | null = null;
    for (const ring of rings.values()) {
      const t = latestTsOf(ring);
      if (t != null && (latest == null || t > latest)) latest = t;
    }
    return latest;
  },

  /**
   * Latest timestamp for boats named in the app selection.
   * Rings are keyed by the server's stream frame id; the app's source_id is a different number,
   * so a caller that passes selection ids to `latestTs` misses a feed that is already drawing.
   */
  latestTsForNames(names: Iterable<string>): number | null {
    const frameIds = new Set<number>();
    for (const name of names) {
      const fid = idByName.get(String(name).toUpperCase().trim());
      if (fid !== undefined) frameIds.add(fid);
    }
    return this.latestTs(frameIds);
  },

  /** Latest timestamp for ONE source (null when it has no retained samples). */
  latestTsFor(sourceId: number): number | null {
    const ring = rings.get(sourceId);
    return ring ? latestTsOf(ring) : null;
  },

  /**
   * Bracketing logical indices around `ms` for one source, for playhead interpolation.
   * Returns null when the source has no data. `frac` in [0,1] between i0 and i1 (i1===i0 at the tail).
   */
  bracket(sourceId: number, ms: number): { i0: number; i1: number; frac: number } | null {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return null;
    const lb = lowerBound(ring, ms); // first index with ts >= ms
    if (lb === 0) return { i0: 0, i1: 0, frac: 0 };
    if (lb >= ring.count) return { i0: ring.count - 1, i1: ring.count - 1, frac: 0 };
    const i0 = lb - 1;
    const i1 = lb;
    const t0 = ring.ts[phys(ring, i0)];
    const t1 = ring.ts[phys(ring, i1)];
    const frac = t1 > t0 ? (ms - t0) / (t1 - t0) : 0;
    return { i0, i1, frac };
  },

  /**
   * Like `bracket`, but only over rows where EVERY channel in `channels` is finite.
   *
   * Rows are built per exact source timestamp and any channel a row did not carry is stored as NaN (see
   * `pushRow`), so the position columns are full of holes whenever another channel samples faster than GPS.
   * Plain `bracket` lands on whatever row precedes the playhead and the caller then has to give up if that
   * one row happens to be a hole — which is how the boat froze at a stale position while `trackCoords`,
   * which simply skips non-finite points, kept drawing the line to the newest fix.
   *
   * Scanning outward from the binary search to the nearest rows that DO carry the channels makes the two
   * readers agree: same points, same straight-line interpolation between them. `frac` is by timestamp
   * across the (possibly non-adjacent) pair and is clamped to [0,1]; `i1 === i0` when only one side exists.
   */
  bracketFinite(
    sourceId: number,
    ms: number,
    channels: readonly string[],
    maxGapMs: number = BRACKET_MAX_GAP_MS
  ): { i0: number; i1: number; frac: number } | null {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return null;
    const cols = columnsFor(ring, channels);
    if (!cols) return null;

    const lb = lowerBound(ring, ms); // first index with ts >= ms
    let i0 = -1;
    for (let i = Math.min(lb, ring.count) - 1; i >= 0; i--) {
      if (ms - ring.ts[phys(ring, i)] > maxGapMs) break;
      if (rowHasAll(ring, i, cols)) {
        i0 = i;
        break;
      }
    }
    let i1 = -1;
    for (let i = lb; i < ring.count; i++) {
      if (ring.ts[phys(ring, i)] - ms > maxGapMs) break;
      if (rowHasAll(ring, i, cols)) {
        i1 = i;
        break;
      }
    }
    if (i0 < 0 && i1 < 0) return null;
    if (i0 < 0) return { i0: i1, i1, frac: 0 };
    if (i1 < 0) return { i0, i1: i0, frac: 0 };

    const t0 = ring.ts[phys(ring, i0)];
    const t1 = ring.ts[phys(ring, i1)];
    const frac = t1 > t0 ? Math.min(1, Math.max(0, (ms - t0) / (t1 - t0))) : 0;
    return { i0, i1, frac };
  },

  /**
   * Newest timestamp at which this source carried ALL of `channels` (null if never).
   *
   * `latestTsFor` answers "did anything arrive?", which is the wrong question for a staleness gate on
   * position: a boat whose GPS died minutes ago keeps looking fresh for as long as any other channel is
   * still flowing, and stays pinned to the map at a position it has long left.
   *
   * `notOlderThanMs` is an optional ABSOLUTE floor (epoch ms): stop scanning once the rows are older
   * than it and report null. Purely an optimisation for callers that are going to reject an old
   * answer anyway — it must never change which value is returned for a row the caller would accept.
   *
   * Deliberately absolute rather than a "max gap": the only caller measures staleness against the
   * PLAYHEAD, and while scrubbing the playhead sits far from the ring's newest sample, so a gap
   * measured from either the tail or the wall clock would truncate perfectly good history. Default
   * is unbounded, which keeps the plain "newest timestamp carrying these channels" contract — that
   * question is about the DATA and is not the right place to decide what counts as fresh.
   *
   * The bound matters because `GlBoatLayer` calls this per boat per `draw()`, and `draw()` is wired
   * to the map's `render` AND `move` events: without a floor, panning over a fleet whose GPS had
   * stopped rescanned the entire ring for every boat on every frame. Harmless at 4096 slots; not
   * harmless once the ring is deep enough to scrub into.
   */
  latestFiniteTsFor(
    sourceId: number,
    channels: readonly string[],
    notOlderThanMs?: number
  ): number | null {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return null;
    const cols = columnsFor(ring, channels);
    if (!cols) return null;
    const floor = Number.isFinite(notOlderThanMs as number) ? (notOlderThanMs as number) : -Infinity;
    for (let i = ring.count - 1; i >= 0; i--) {
      // Rows are ascending, so once one is below the floor every earlier one is too.
      if (ring.ts[phys(ring, i)] < floor) break;
      if (rowHasAll(ring, i, cols)) return ring.ts[phys(ring, i)];
    }
    return null;
  },

  /** Channel value at a logical index (NaN if channel/idx absent). */
  valueAt(sourceId: number, channel: string, logicalIdx: number): number {
    const ring = rings.get(sourceId);
    if (!ring || logicalIdx < 0 || logicalIdx >= ring.count) return NaN;
    const col = ring.channels.get(channel);
    return col ? col[phys(ring, logicalIdx)] : NaN;
  },

  tsAt(sourceId: number, logicalIdx: number): number {
    const ring = rings.get(sourceId);
    if (!ring || logicalIdx < 0 || logicalIdx >= ring.count) return NaN;
    return ring.ts[phys(ring, logicalIdx)];
  },

  /** [lng,lat] coordinates within [startMs,endMs] for a source (for a GeoJSON LineString). */
  trackCoords(sourceId: number, startMs: number, endMs: number, latCh = 'Lat_dd', lngCh = 'Lng_dd'): number[][] {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return [];
    const lat = ring.channels.get(latCh);
    const lng = ring.channels.get(lngCh);
    if (!lat || !lng) return [];
    const from = lowerBound(ring, startMs);
    const out: number[][] = [];
    for (let i = from; i < ring.count; i++) {
      const p = phys(ring, i);
      if (ring.ts[p] > endMs) break;
      const la = lat[p];
      const lo = lng[p];
      if (Number.isFinite(la) && Number.isFinite(lo)) out.push([lo, la]);
    }
    return out;
  },

  /**
   * Window of row objects for a source within [startMs,endMs], each `{ timestamp, Datetime, ...channels }`
   * (finite channel values only). Shape matches the legacy getFilteredData rows so `buildLiveFrameSnapshot`
   * (wind / bad-air / BSP overlays) consumes it unchanged. Allocates per call — bounded by the window.
   */
  windowRows(sourceId: number, startMs: number, endMs: number): any[] {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return [];
    const from = lowerBound(ring, startMs);
    const out: any[] = [];
    for (let i = from; i < ring.count; i++) {
      const p = phys(ring, i);
      const ts = ring.ts[p];
      if (ts > endMs) break;
      const row: any = { timestamp: ts, Datetime: new Date(ts) };
      for (const [name, col] of ring.channels) {
        const v = col[p];
        if (Number.isFinite(v)) row[name] = v;
      }
      out.push(row);
    }
    return out;
  },

  /** Logical-ordered copy of {ts, values} for one channel over a source (for uPlot AlignedData). */
  orderedColumn(sourceId: number, channel: string): { ts: Float64Array; values: Float64Array } | null {
    const ring = rings.get(sourceId);
    if (!ring || ring.count === 0) return null;
    const col = ring.channels.get(channel);
    if (!col) return null;
    const ts = new Float64Array(ring.count);
    const values = new Float64Array(ring.count);
    for (let i = 0; i < ring.count; i++) {
      const p = phys(ring, i);
      ts[i] = ring.ts[p];
      values[i] = col[p];
    }
    return { ts, values };
  },

  /**
   * Drop every retained sample and the source registry.
   *
   * Production use: the server sends a `stream_reset` control message on a session boundary (simulation
   * start/stop), because data from the previous session must not survive into the next one — a replay's
   * boats are in the wrong place entirely, and sim samples are stamped on wall-clock time so nothing else
   * would ever age them out. Also used by tests.
   */
  clearAll(): void {
    rings.clear();
    nameById.clear();
    idByName.clear();
    // Release the coalescing latch too: a scheduled callback that never runs (a rAF queued in a tab
    // backgrounded before it fired, or a faked timer that never advances) would otherwise leave this
    // true and suppress every later bump.
    epochScheduled = false;
    setEpoch((v) => v + 1); // synchronous: consumers must repaint empty now, not at the next frame
  },

  /** @deprecated Use `clearAll()`. Retained for existing test call-sites. */
  _clear(): void {
    liveColumnarStore.clearAll();
  },
};
